import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import MaterialIcon from "../../components/MaterialIcon";
import LikesDropdown from "../../components/LikesDropdown";
import PdfViewerModal from "../../components/PdfViewerModal";
import VideoPreviewThumb from "../../components/chatMedia/VideoPreviewThumb";
import { HighlightText } from "../../helper/highlightSearch";
import { bodyBlocks, imageUrlOf, splitMedia, tr, youtubeIdOf } from "./newsProModel";
import "./NewsPro.css";

// Kjuup Pro News pieces (web port of the mobile HomeGlassProParts, KPNews.dc.html "News (tab 2)"):
// the title row, the audience card (External / Internal, company filter, documents), the article
// card with its 16:9 media, the card menu and the feed chrome. Prefix np-.

/* ---- Title row ---------------------------------------------------------------------------- */

/**
 * "News" + manage_search (article search of the feed shown) + the primary "Post" button. While
 * the search is open the row becomes the search field, with the match count inside it and Cancel.
 */
export function NewsTitleBar({ title, search, post, strings }) {
  if (search.open) {
    return (
      <div className="np-titlebar np-titlebar--search">
        <div className="np-search">
          <MaterialIcon icon="search" size={20} className="np-search-icon" />
          <input
            className="np-search-input"
            type="text"
            value={search.value}
            placeholder={search.placeholder}
            onChange={(e) => search.onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") search.onClose();
            }}
            autoFocus
          />
          {search.countLabel ? <span className="np-search-count">{search.countLabel}</span> : null}
          {search.value ? (
            <button
              type="button"
              className="np-search-clear"
              onClick={() => search.onChange("")}
              aria-label={tr(strings, "pro_news_clear_search", "Clear search")}
              title={tr(strings, "pro_news_clear_search", "Clear search")}
            >
              <MaterialIcon icon="cancel" size={18} fill={1} />
            </button>
          ) : null}
        </div>
        <button type="button" className="kj-btn kj-btn--ghost np-search-cancel" onClick={search.onClose}>
          {tr(strings, "cancel", "Cancel")}
        </button>
      </div>
    );
  }
  return (
    <div className="np-titlebar">
      <h2 className="np-title">{title}</h2>
      <div className="np-title-actions">
        <button
          type="button"
          className="kj-btn kj-btn--icon kj-btn--lg kj-btn--secondary np-title-btn"
          onClick={search.onToggle}
          title={search.label}
          aria-label={search.label}
        >
          <MaterialIcon icon="manage_search" size={22} />
        </button>
        {post ? (
          <button type="button" className="kj-btn kj-btn--primary np-title-btn np-post-btn" onClick={post.onClick} title={post.label}>
            <MaterialIcon icon="edit_note" size={22} />
            <span>{post.label}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ---- Audience card ------------------------------------------------------------------------ */

/** External / Internal: chip track, 38 items, a 17 icon (filled when selected), label and the orange count. */
export function FeedSegmentPro({ items, value, onPress }) {
  return (
    <div className="np-seg" role="tablist">
      {items.map((it) => {
        const on = it.key === value;
        const count = Number(it.count) || 0;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={on}
            aria-label={count > 0 ? `${it.label}, ${count}` : it.label}
            className={`np-seg-item${on ? " is-active" : ""}`}
            onClick={() => onPress && onPress(it.key)}
          >
            <MaterialIcon icon={it.icon} size={17} fill={on ? 1 : 0} />
            <span className="np-seg-label">{it.label}</span>
            {count > 0 ? <span className="np-counter">{count > 99 ? "99+" : count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The company bar (as in Rooms): the selected company's logo and "Showing articles of …"; opens
 * the company sheet. `otherNew` = unseen posts in the other companies (the orange count, its label
 * under the name).
 */
export function CompanyFilterBar({ company, label, otherNew, otherNewLabel, onClick }) {
  return (
    <button type="button" className="np-company" onClick={onClick} aria-haspopup="dialog">
      <ProLogo head={{ kind: "company", name: company.name, logoUri: company.logoUri, initials: company.initials }} size={30} />
      <span className="np-company-text">
        <span className="np-company-name">{label}</span>
        {otherNew > 0 ? <span className="np-company-sub">{otherNewLabel}</span> : null}
      </span>
      {otherNew > 0 ? <span className="np-counter">{otherNew > 99 ? "99+" : otherNew}</span> : null}
      <MaterialIcon icon="unfold_more" size={22} className="np-faint" />
    </button>
  );
}

/** The card that holds the feed segment, the company filter and the documents row, separated by hairlines. */
export function AudienceCardPro({ segment, company, documents }) {
  if (!segment && !company && !documents) return null;
  return (
    <div className="np-card np-audience">
      {segment ? <div className="np-audience-seg">{segment}</div> : null}
      {company ? (
        <>
          {segment ? <div className="np-hairline" /> : null}
          {company}
        </>
      ) : null}
      {documents ? (
        <>
          {segment || company ? <div className="np-hairline" /> : null}
          {documents}
        </>
      ) : null}
    </div>
  );
}

/* ---- Small pieces ------------------------------------------------------------------------- */

/**
 * Author mark of a card (38). Company: radius 10 tile in ink with the initials, or its logo on
 * surface with a 1px line. Person: the round pastel avatar or the photo.
 */
export function ProLogo({ head, size = 38 }) {
  const [failed, setFailed] = useState(false);
  const uri = head && head.logoUri;
  useEffect(() => setFailed(false), [uri]);
  if (!head) return null;
  const photo = !!uri && !failed;
  const person = head.kind === "person";
  const cls = `np-logo np-logo--${size}${person ? " np-logo--person" : ""}${photo ? " np-logo--photo" : ""}`;
  return (
    <span className={cls} aria-hidden="true">
      {photo ? <img src={uri} alt="" onError={() => setFailed(true)} /> : head.initials || (person ? <MaterialIcon icon="person" size={20} /> : "·")}
    </span>
  );
}

/** The design's Pinned tag: keep (filled) + label on primarySoft. */
export function PinnedTag({ label }) {
  return (
    <span className="np-tag np-tag--new">
      <MaterialIcon icon="keep" size={14} fill={1} />
      {label}
    </span>
  );
}

/* ---- Media -------------------------------------------------------------------------------- */

function MediaTile({ entry }) {
  const { kind, item } = entry;
  if (kind === "image") return <img className="np-media-img" src={imageUrlOf(item)} alt="" loading="lazy" />;
  if (kind === "video") return <VideoPreviewThumb item={item} className="np-media-video" playSize={44} />;
  const yt = kind === "youtube" ? youtubeIdOf(item.href) : null;
  return (
    <span className="np-media-embed">
      {yt ? <img className="np-media-img" src={`https://img.youtube.com/vi/${yt}/hqdefault.jpg`} alt="" loading="lazy" /> : null}
      <span className="np-media-embed-label">
        <MaterialIcon icon="play_circle" size={44} fill={1} />
        {!yt ? <span>{item.videoType === "vimeo" ? "Vimeo" : "YouTube"}</span> : null}
      </span>
    </span>
  );
}

/**
 * The 16:9 media of a card: full width, hairlines above and below. Several photos / videos page
 * sideways (arrows on hover) with a "1 / 3" chip; a click reports the item's index in the pager list.
 */
export function MediaPager({ visual, onOpen, label, strings }) {
  const [page, setPage] = useState(0);
  const count = visual ? visual.length : 0;
  useEffect(() => {
    if (page > count - 1) setPage(0);
  }, [count, page]);
  if (!count) return null;
  const many = count > 1;
  const entry = visual[Math.min(page, count - 1)];
  const go = (e, delta) => {
    e.stopPropagation();
    setPage((p) => (p + delta + count) % count);
  };
  return (
    <div className="np-media">
      <div
        role="button"
        tabIndex={0}
        className="np-media-tile"
        onClick={(e) => {
          e.stopPropagation();
          if (onOpen) onOpen(page);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            if (onOpen) onOpen(page);
          }
        }}
        aria-label={label}
      >
        <MediaTile key={String(entry.index)} entry={entry} />
      </div>
      {many ? (
        <>
          <span className="np-media-chip" aria-hidden="true">{`${page + 1} / ${count}`}</span>
          <button type="button" className="np-media-nav np-media-nav--prev" onClick={(e) => go(e, -1)} aria-label={tr(strings, "previous", "Previous")}>
            <MaterialIcon icon="chevron_left" size={22} />
          </button>
          <button type="button" className="np-media-nav np-media-nav--next" onClick={(e) => go(e, 1)} aria-label={tr(strings, "next", "Next")}>
            <MaterialIcon icon="chevron_right" size={22} />
          </button>
        </>
      ) : null}
    </div>
  );
}

/** PDF attachments of an article: 44 rows (radius 12, 1px line) with the red PDF glyph and the file name. */
export function FileList({ files }) {
  const [open, setOpen] = useState(null);
  if (!files || !files.length) return null;
  return (
    <div className="np-files">
      {files.map((f) => (
        <button
          key={String(f.index)}
          type="button"
          className="np-file"
          onClick={(e) => {
            e.stopPropagation();
            if (f.url) setOpen(f);
          }}
          title={f.name}
        >
          <MaterialIcon icon="picture_as_pdf" size={22} className="np-err" />
          <span className="np-file-name">{f.name}</span>
          <MaterialIcon icon="chevron_right" size={22} className="np-faint" />
        </button>
      ))}
      {open ? <PdfViewerModal url={open.url} fileName={open.name} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

/* ---- Article card ------------------------------------------------------------------------- */

/**
 * The card's body: 3 lines, then "Read more" when the text is longer; it expands in place
 * ("Show less" folds it).
 */
function ClampedBody({ text, term, strings }) {
  const ref = useRef(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [text]);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || open) return undefined;
    const measure = () => setLong(node.scrollHeight - node.clientHeight > 2);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [text, open]);
  return (
    <div>
      <div ref={ref} className={`np-body${open ? "" : " is-clamped"}`}>
        {bodyBlocks(text).map((b, i) =>
          b.kind === "li" ? (
            <div key={i} className="np-li" data-mark={b.mark}>
              <HighlightText text={b.text} query={term} />
            </div>
          ) : (
            <div key={i} className="np-p">
              <HighlightText text={b.text} query={term} />
            </div>
          )
        )}
      </div>
      {long || open ? (
        <button
          type="button"
          className="np-read-more"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {open ? tr(strings, "show_less", "Show less") : tr(strings, "pro_news_read_more", "Read more")}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Like action: thumb (toggles the like) and the count (opens the likes list), 0 included;
 * liked = filled in primaryText. Own posts cannot be liked.
 */
function CardLike({ card, articleId, likes, onLike, likesSignal, strings }) {
  const n = card.likeCount;
  const countLabel = fill99(n);
  const count = <span className={`np-like-count${card.liked ? " is-on" : ""}`}>{countLabel}</span>;
  return (
    <div className="np-like-row">
      <button
        type="button"
        className={`np-like${card.liked ? " is-on" : ""}`}
        disabled={card.likeDisabled}
        onClick={(e) => {
          e.stopPropagation();
          if (onLike) onLike();
        }}
        aria-pressed={card.liked}
        aria-label={tr(strings, "like", "Like")}
        title={tr(strings, "like", "Like")}
      >
        <MaterialIcon icon="thumb_up" size={22} fill={card.liked ? 1 : 0} />
      </button>
      {n > 0 ? (
        <LikesDropdown articleId={articleId} likes={likes} likeCount={n} openSignal={likesSignal}>
          <button
            type="button"
            className="np-like-count-btn"
            aria-label={fill(tr(strings, "pro_news_show_likes", "Show likes ({0})"), n)}
            title={fill(tr(strings, "pro_news_show_likes", "Show likes ({0})"), n)}
          >
            {count}
          </button>
        </LikesDropdown>
      ) : (
        <span className="np-like-count-btn is-static">{count}</span>
      )}
    </div>
  );
}

const fill = (s, n) => String(s).replace("{0}", n);
const fill99 = (n) => (n > 9999 ? "9999+" : String(Math.max(0, Number(n) || 0)));

/** Card menu (more_vert): a popover of menu rows; closes on an outside click or Escape. */
function CardMenu({ rows, label, busy }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="np-menu-wrap" ref={root}>
      <button
        type="button"
        className={`np-more${open ? " is-open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        aria-busy={!!busy}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {busy ? <span className="np-spinner" /> : <MaterialIcon icon="more_vert" size={22} />}
      </button>
      {open ? (
        <div className="np-menu" role="menu">
          {rows.map((r) => (
            <button
              key={r.key}
              type="button"
              role="menuitem"
              className={`np-menu-row${r.danger ? " is-danger" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                r.onClick();
              }}
            >
              <MaterialIcon icon={r.icon} size={22} />
              <span>{r.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Department / location chips (internal posts) and the "Product: …" line. */
function ArticleMetaPro({ tags, product, strings }) {
  if (!tags.length && !product) return null;
  return (
    <div className="np-meta">
      {tags.length ? (
        <div className="np-meta-tags">
          {tags.map((x) => (
            <span key={x.key} className="np-tag np-tag--muted">
              {x.label}
            </span>
          ))}
        </div>
      ) : null}
      {product ? <div className="np-meta-product">{`${tr(strings, "product_label", "Product:")} ${product}`}</div> : null}
    </div>
  );
}

/**
 * Article card (KPNews 2i): surface, 1px line, radius 16. Header: 38 logo, name over
 * "author · time", the Pinned tag and more_vert. Then the clickable body (16:9 media, title,
 * 3-line text), PDFs, and the like control.
 */
export function ArticleCardPro({
  article,
  card,
  media,
  isNew,
  pinned,
  pinBusy,
  highlighted,
  searchTerm,
  strings,
  menuRows,
  likesSignal,
  onLike,
  onAuthor,
  onPoster,
  onOpenMedia,
}) {
  const { visual, files } = splitMedia(media);
  const head = card.head || {};
  const moreLabel = tr(strings, "pro_news_more", "More");
  const authorLabel = [head.name, head.sub].filter(Boolean).join(", ");
  return (
    <article
      className={`np-card np-article${highlighted ? " is-highlighted" : ""}`}
      aria-label={isNew ? `${card.title || head.name} (${tr(strings, "article_new_badge", "New")})` : undefined}
    >
      <div className="np-head">
        <button type="button" className="np-head-main" onClick={onAuthor} disabled={!onAuthor} aria-label={authorLabel}>
          <ProLogo head={head} size={38} />
          <span className="np-head-text">
            <span className="np-head-name">{head.name}</span>
            {head.sub ? (
              <span className="np-head-sub">
                {onPoster && head.poster ? (
                  <>
                    <span
                      role="link"
                      tabIndex={0}
                      className="np-poster"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPoster();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          onPoster();
                        }
                      }}
                    >
                      {head.poster}
                    </span>
                    {head.time ? ` · ${head.time}` : ""}
                  </>
                ) : (
                  head.sub
                )}
              </span>
            ) : null}
          </span>
        </button>
        {pinned ? <PinnedTag label={tr(strings, "pro_news_pinned", "Pinned")} /> : null}
        {menuRows && menuRows.length ? <CardMenu rows={menuRows} label={moreLabel} busy={pinBusy} /> : <span className="np-more-gap" />}
      </div>
      <ArticleMetaPro tags={card.tags} product={card.product} strings={strings} />
      <MediaPager visual={visual} onOpen={(i) => onOpenMedia(visual, i)} label={card.title} strings={strings} />
      {card.title || card.plain ? (
        <div className="np-text">
          {card.title ? (
            <div className="np-card-title">
              <HighlightText text={card.title} query={searchTerm} />
            </div>
          ) : null}
          {card.plain ? <ClampedBody text={card.plain} term={searchTerm} strings={strings} /> : null}
        </div>
      ) : null}
      <FileList files={files} />
      <div className="np-actions">
        <CardLike card={card} articleId={article.id} likes={article.likes} onLike={onLike} likesSignal={likesSignal} strings={strings} />
      </div>
    </article>
  );
}

/* ---- Feed chrome -------------------------------------------------------------------------- */

/** "↑ 3 new posts" over the feed: primary pill, white arrow + label, the floating shadow. */
export function NewPostsPillPro({ count, onClick, strings }) {
  if (!(count > 0)) return null;
  const label = fill(tr(strings, "article_new_posts", "{0} new posts"), count);
  return (
    <button type="button" className="kj-btn kj-btn--primary kj-btn--pill np-new-pill" onClick={onClick}>
      <MaterialIcon icon="arrow_upward" size={18} />
      <span>{label}</span>
    </button>
  );
}

/** First load: two article cards in shimmering bones in the card's real layout. */
export function NewsSkeletonPro({ count = 2 }) {
  return (
    <div className="np-skel" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="np-card">
          <div className="np-skel-head">
            <span className="np-bone np-bone--logo" />
            <span className="np-skel-lines">
              <span className="np-bone np-bone--w55 np-bone--h12" />
              <span className="np-bone np-bone--w35" />
            </span>
          </div>
          <div className="np-bone np-bone--media">
            <MaterialIcon icon="image" size={36} />
          </div>
          <div className="np-skel-text">
            <span className="np-bone np-bone--w75 np-bone--h14" />
            <span className="np-bone" />
            <span className="np-bone np-bone--w90" />
            <span className="np-bone np-bone--w55" />
          </div>
          <div className="np-skel-actions">
            <span className="np-bone np-bone--like" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Empty / error state: icon in a soft circle, title, hint and an optional button. */
export function NewsEmptyPro({ icon, title, hint, action, onAction }) {
  return (
    <div className="np-empty">
      <span className="np-empty-icon">
        <MaterialIcon icon={icon} size={28} />
      </span>
      <div className="np-empty-title">{title}</div>
      {hint ? <div className="np-empty-hint">{hint}</div> : null}
      {action ? (
        <button type="button" className="kj-btn kj-btn--primary kj-btn--sm" onClick={onAction}>
          {action}
        </button>
      ) : null}
    </div>
  );
}
